import {
  buildEditorSubsectionSummaryRows,
  editorSubsectionFieldKeys,
  editorSubsectionFieldsExcluding,
} from './projections/editorSubsectionSummaryRows';
import { createStepSummaryBridge } from './projections/stepSummaryBridge';
import {
  subsectionDescriptorsFromFields,
  subsectionFieldKeys,
} from './projections/subsectionDescriptors';
import {
  buildSummaryFieldMappingsFromRows,
  buildSummaryRowsFromFields,
  enrichSummaryRowsWithRbac,
} from './projections/summaryRows';
import { resolveRbacKeysForDataField } from './projections/summaryRows';
import { COOLING_READINESS_FIELDS } from './steps/cooling-readiness.fields';
import { FACILITY_POWER_FIELDS } from './steps/facility-power.fields';
import { LIQUID_COOLING_FIELDS } from './steps/liquid-cooling.fields';
import { MONITORING_MANAGEMENT_FIELDS } from './steps/monitoring-management.fields';
import {
  OPPORTUNITY_FIELDS,
  type OpportunitySubsectionId,
} from './steps/opportunity.fields';
import { RACK_POWER_FIELDS } from './steps/rack-power.fields';
import { SITE_INFORMATION_FIELDS } from './steps/site-information.fields';
import { SMALL_UPS_FIELDS } from './steps/small-ups.fields';
import { RACK_EDIT_RACK_TYPE_LABEL } from '../../constants/rackConfigurationsSubsectionCopy';
import { RACK_POWER_FIELD_LABELS } from '../../constants/rackPowerFieldLabels';

import type { FormStepId, RequestFieldDefinition } from './types';
import type { SummaryCardRow } from '../../summary/summaryCardSourceTypes';
import type { SubsectionFieldDescriptor } from '../../utils/subsectionHeaderMetrics';

export type {
  RequestFieldDefinition,
  RequestFieldSummaryRow,
  FormStepId,
} from './types';
export type { OpportunitySubsectionId } from './steps/opportunity.fields';
export {
  OPPORTUNITY_FIELDS,
  OPPORTUNITY_SUBSECTION_DISPLAY_TITLES,
  OPPORTUNITY_SUBSECTION_ORDER,
} from './steps/opportunity.fields';
export { SITE_INFORMATION_FIELDS } from './steps/site-information.fields';
export { FACILITY_POWER_FIELDS } from './steps/facility-power.fields';
export { RACK_POWER_FIELDS } from './steps/rack-power.fields';
export { SMALL_UPS_FIELDS } from './steps/small-ups.fields';
export { COOLING_READINESS_FIELDS } from './steps/cooling-readiness.fields';
export { LIQUID_COOLING_FIELDS } from './steps/liquid-cooling.fields';
export { MONITORING_MANAGEMENT_FIELDS } from './steps/monitoring-management.fields';

const STEP_FIELDS: Readonly<
  Record<FormStepId, readonly RequestFieldDefinition[]>
> = {
  opportunity: OPPORTUNITY_FIELDS,
  'site-information': SITE_INFORMATION_FIELDS,
  'facility-power': FACILITY_POWER_FIELDS,
  'rack-power': RACK_POWER_FIELDS,
  'small-ups': SMALL_UPS_FIELDS,
  // Large UPS shares the facility-power registry (its per-unit fields carry
  // `stepId: 'facility-power'`), so the standalone 'large-ups' step has no
  // dedicated field file — `[]` preserves the pre-union `?? []` behavior
  // (#997 Story 6.5 added 'large-ups' to FormStepId).
  'large-ups': [],
  // Power Distribution shares the facility-power registry (its per-unit fields
  // carry `stepId: 'facility-power'`), so the standalone 'power-distribution' step
  // has no dedicated field file — `[]` preserves the pre-union `?? []` behavior
  // (#997 Story 6.7 added 'power-distribution' to FormStepId).
  'power-distribution': [],
  'cooling-readiness': COOLING_READINESS_FIELDS,
  'liquid-cooling': LIQUID_COOLING_FIELDS,
  documents: [],
  summary: [],
};

export function getFieldsForStep(
  stepId: FormStepId,
): readonly RequestFieldDefinition[] {
  return STEP_FIELDS[stepId] ?? [];
}

/**
 * Global `dataField → UI label` map across every step's field registry. For surfaces that only
 * have the field key (e.g. Version History deltas). First definition wins for fields shared across
 * steps. This is the label users actually see on the form — NOT `fieldMapping.jsonField`, which is
 * a coarse RBAC permission bucket shared by several fields (#1148 Issue 22).
 */
const REGISTRY_LABEL_BY_DATA_FIELD: ReadonlyMap<string, string> = (() => {
  const map = new Map<string, string>();
  for (const fields of Object.values(STEP_FIELDS)) {
    for (const field of fields) {
      if (!map.has(field.dataField)) map.set(field.dataField, field.label);
    }
  }
  return map;
})();

/** UI label for a field key from the request field registry, or null when not registered. */
export function getRegistryFieldLabel(dataField?: string): string | null {
  return (dataField && REGISTRY_LABEL_BY_DATA_FIELD.get(dataField)) || null;
}

export function getOpportunityFieldDefinitions(): readonly RequestFieldDefinition[] {
  return OPPORTUNITY_FIELDS;
}

export function getOpportunitySubsectionFieldDescriptors(
  subsectionId: OpportunitySubsectionId,
) {
  return subsectionDescriptorsFromFields(
    OPPORTUNITY_FIELDS,
    subsectionId,
    'step',
  );
}

export const OPPORTUNITY_REQUESTOR_SUBSECTION_FIELDS =
  getOpportunitySubsectionFieldDescriptors('requestor');
export const OPPORTUNITY_PROJECT_SUMMARY_SUBSECTION_FIELDS =
  getOpportunitySubsectionFieldDescriptors('projectSummary');
export const OPPORTUNITY_DEAL_REGISTRATION_SUBSECTION_FIELDS =
  getOpportunitySubsectionFieldDescriptors('projectDetails');
export const OPPORTUNITY_SCALE_TIMELINE_SUBSECTION_FIELDS =
  getOpportunitySubsectionFieldDescriptors('scaleTimelineBudget');
export const OPPORTUNITY_NOTES_SUBSECTION_FIELDS =
  getOpportunitySubsectionFieldDescriptors('additionalInformation');
export const OPPORTUNITY_TEAM_SUBSECTION_FIELDS =
  getOpportunitySubsectionFieldDescriptors('team');

export const OPPORTUNITY_REQUESTOR_FIELD_KEYS = subsectionFieldKeys(
  OPPORTUNITY_REQUESTOR_SUBSECTION_FIELDS,
);
export const OPPORTUNITY_PROJECT_SUMMARY_FIELD_KEYS = subsectionFieldKeys(
  OPPORTUNITY_PROJECT_SUMMARY_SUBSECTION_FIELDS,
);
export const OPPORTUNITY_DEAL_REGISTRATION_FIELD_KEYS = subsectionFieldKeys(
  OPPORTUNITY_DEAL_REGISTRATION_SUBSECTION_FIELDS,
);
export const OPPORTUNITY_SCALE_TIMELINE_FIELD_KEYS = subsectionFieldKeys(
  OPPORTUNITY_SCALE_TIMELINE_SUBSECTION_FIELDS,
);
export const OPPORTUNITY_NOTES_FIELD_KEYS = subsectionFieldKeys(
  OPPORTUNITY_NOTES_SUBSECTION_FIELDS,
);
export const OPPORTUNITY_TEAM_FIELD_KEYS = subsectionFieldKeys(
  OPPORTUNITY_TEAM_SUBSECTION_FIELDS,
);

function subsectionDescriptors(
  fields: readonly RequestFieldDefinition[],
  subsectionId: string,
) {
  return subsectionDescriptorsFromFields(fields, subsectionId, 'step');
}

export const SITE_DIMENSIONS_SUBSECTION_FIELDS = subsectionDescriptors(
  SITE_INFORMATION_FIELDS,
  'siteDimensions',
).filter(f => f.key !== 'siteAdditionalSizeInformation');
export const SITE_INFORMATION_NOTES_SUBSECTION_FIELDS = [
  { key: 'siteAdditionalSizeInformation', shortLabel: 'Notes' },
] as const;

export const FACILITY_POWER_NOTES_SUBSECTION_FIELDS = [
  { key: 'powerAdditionalInformation', shortLabel: 'Notes' },
] as const;

/** Large UPS step — shared request notes (same field as FP family; step-scoped label). */
export const LARGE_UPS_NOTES_SUBSECTION_FIELDS = [
  { key: 'powerAdditionalInformation', shortLabel: 'Notes' },
] as const;

export const SMALL_UPS_NOTES_SUBSECTION_FIELDS = [
  { key: 'powerSmallUpsAdditionalInfo', shortLabel: 'Notes' },
] as const;

export const COOLING_NOTES_SUBSECTION_FIELDS = [
  { key: 'coolingAdditionalReadinessInfo', shortLabel: 'Notes' },
] as const;

export const FACILITY_POWER_SUBSECTION_FIELDS = subsectionDescriptors(
  FACILITY_POWER_FIELDS,
  'facilityPower',
);
export const FACILITY_POWER_LARGE_UPS_SUBSECTION_FIELDS = subsectionDescriptors(
  FACILITY_POWER_FIELDS,
  'largeUps',
);
/**
 * Per-instance Large UPS editor card. #997 QA-A2: `powerSourceLocation` and
 * `powerFireSuppressionNeeds` are now PER-UNIT (migration 111), so they flow
 * through here (formerly filtered out as request-level shared fields).
 */
export const LARGE_UPS_EDITOR_SUBSECTION_FIELDS = [
  { key: 'name', shortLabel: 'Name' },
  { key: 'status', shortLabel: 'Type' },
  { key: 'locationTagId', shortLabel: 'Location' },
  ...FACILITY_POWER_LARGE_UPS_SUBSECTION_FIELDS,
] as const;
export const FACILITY_POWER_DISTRIBUTION_SUBSECTION_FIELDS =
  subsectionDescriptors(FACILITY_POWER_FIELDS, 'powerDistribution').filter(
    f => f.key !== 'powerAdditionalInformation',
  );
/** Per-instance Power Distribution editor main card. */
export const POWER_DISTRIBUTION_EDITOR_SUBSECTION_FIELDS = [
  { key: 'name', shortLabel: 'Name' },
  { key: 'status', shortLabel: 'Type' },
  { key: 'locationTagId', shortLabel: 'Location' },
  ...FACILITY_POWER_DISTRIBUTION_SUBSECTION_FIELDS,
] as const;
export const RACK_INFORMATION_SUBSECTION_FIELDS = subsectionDescriptors(
  RACK_POWER_FIELDS,
  'rackInformation',
);
export const RACK_SPECS_SUBSECTION_FIELDS = subsectionDescriptors(
  RACK_POWER_FIELDS,
  'specs',
);
export const RACK_POWER_INFO_SUBSECTION_FIELDS = subsectionDescriptors(
  RACK_POWER_FIELDS,
  'rackElectrical',
);
export const RACK_POWER_NOTES_SUBSECTION_FIELDS = subsectionDescriptors(
  RACK_POWER_FIELDS,
  'additionalInformation',
);

export const SMALL_UPS_MAIN_SUBSECTION_FIELDS = subsectionDescriptors(
  SMALL_UPS_FIELDS,
  'requirements',
).filter(f => f.key !== 'powerSmallUpsAdditionalInfo');

/** Per-instance Small UPS editor main card (name/type + requirements fields). */
export const SMALL_UPS_EDITOR_SUBSECTION_FIELDS = [
  { key: 'name', shortLabel: 'Name' },
  { key: 'status', shortLabel: 'Type' },
  { key: 'locationTagId', shortLabel: 'Location' },
  ...SMALL_UPS_MAIN_SUBSECTION_FIELDS,
] as const;

export const COOLING_MAIN_SUBSECTION_FIELDS = subsectionDescriptors(
  COOLING_READINESS_FIELDS,
  'coolingInformation',
).filter(f => f.key !== 'coolingAdditionalReadinessInfo');

/** Per-instance cooling system editor main card. */
export const COOLING_SYSTEM_EDITOR_SUBSECTION_FIELDS = [
  { key: 'name', shortLabel: 'Name' },
  { key: 'status', shortLabel: 'Type' },
  { key: 'locationTagId', shortLabel: 'Location' },
  ...COOLING_MAIN_SUBSECTION_FIELDS,
] as const;

export const LIQUID_COOLING_PROJECT_SUBSECTION_FIELDS = subsectionDescriptors(
  LIQUID_COOLING_FIELDS,
  'project',
);
export const LIQUID_COOLING_CDU_EDITOR_SUBSECTION_FIELDS = [
  { key: 'lcLiquidToChipDesired', shortLabel: 'Liquid-to-chip' },
  { key: 'name', shortLabel: 'Name' },
  { key: 'status', shortLabel: 'Type' },
  { key: 'locationTagId', shortLabel: 'Location' },
  ...LIQUID_COOLING_PROJECT_SUBSECTION_FIELDS.filter(
    field => field.key !== 'lcLiquidToChipDesired',
  ),
] as const;

/**
 * Request-level cooling fields counted once in the editor badge.
 * #997 Bug 9: `coolingIsChilledWaterAvailable` is now PER-INSTANCE (each cooling unit
 * owns its own value), so there are no shared cooling fields — this list is empty and
 * the field is counted/rendered per unit like the rest of the cooling fields.
 */
export const COOLING_EDITOR_SHARED_FIELD_KEYS = [] as const;
export const LIQUID_COOLING_CDU_EDITOR_SHARED_FIELD_KEYS = [
  'lcLiquidToChipDesired',
] as const;

const COOLING_EDITOR_SHARED_FIELD_SET = new Set<string>(
  COOLING_EDITOR_SHARED_FIELD_KEYS,
);
const LIQUID_COOLING_CDU_EDITOR_SHARED_FIELD_SET = new Set<string>(
  LIQUID_COOLING_CDU_EDITOR_SHARED_FIELD_KEYS,
);

export const LARGE_UPS_PER_INSTANCE_EDITOR_FIELD_KEYS =
  editorSubsectionFieldKeys(LARGE_UPS_EDITOR_SUBSECTION_FIELDS);
export const POWER_DISTRIBUTION_PER_INSTANCE_EDITOR_FIELD_KEYS =
  editorSubsectionFieldKeys(POWER_DISTRIBUTION_EDITOR_SUBSECTION_FIELDS);
export const SMALL_UPS_PER_INSTANCE_EDITOR_FIELD_KEYS =
  editorSubsectionFieldKeys(SMALL_UPS_EDITOR_SUBSECTION_FIELDS);
export const COOLING_PER_INSTANCE_EDITOR_FIELD_KEYS = editorSubsectionFieldKeys(
  editorSubsectionFieldsExcluding(
    COOLING_SYSTEM_EDITOR_SUBSECTION_FIELDS,
    COOLING_EDITOR_SHARED_FIELD_SET,
  ),
);
export const LIQUID_COOLING_CDU_PER_INSTANCE_EDITOR_FIELD_KEYS =
  editorSubsectionFieldKeys(
    editorSubsectionFieldsExcluding(
      LIQUID_COOLING_CDU_EDITOR_SUBSECTION_FIELDS,
      LIQUID_COOLING_CDU_EDITOR_SHARED_FIELD_SET,
    ),
  );

const largeUpsEditorSummaryRows = buildEditorSubsectionSummaryRows({
  editorFields: LARGE_UPS_EDITOR_SUBSECTION_FIELDS,
  registryFields: FACILITY_POWER_FIELDS,
  rbacAnchorDataField: 'powerExistingSiteProtectedByLargeKwUps',
  subsectionId: 'largeUps',
});

const powerDistributionEditorSummaryRows = buildEditorSubsectionSummaryRows({
  editorFields: POWER_DISTRIBUTION_EDITOR_SUBSECTION_FIELDS,
  registryFields: FACILITY_POWER_FIELDS,
  rbacAnchorDataField: 'powerUpsOutputVoltage',
  subsectionId: 'powerDistribution',
});

const smallUpsEditorSummaryRows = buildEditorSubsectionSummaryRows({
  editorFields: SMALL_UPS_EDITOR_SUBSECTION_FIELDS,
  registryFields: SMALL_UPS_FIELDS,
  rbacAnchorDataField: 'smallUpsManufacturer',
  subsectionId: 'requirements',
});

const coolingSystemEditorSummaryRows = buildEditorSubsectionSummaryRows({
  editorFields: editorSubsectionFieldsExcluding(
    COOLING_SYSTEM_EDITOR_SUBSECTION_FIELDS,
    COOLING_EDITOR_SHARED_FIELD_SET,
  ),
  registryFields: COOLING_READINESS_FIELDS,
  rbacAnchorDataField: 'coolingIsExistingSystemOnsite',
  subsectionId: 'coolingInformation',
});

const coolingEditorSharedSummaryRows = buildEditorSubsectionSummaryRows({
  editorFields: COOLING_EDITOR_SHARED_FIELD_KEYS.map(key => ({
    key,
    shortLabel:
      COOLING_READINESS_FIELDS.find(field => field.dataField === key)
        ?.shortLabel ?? key,
  })),
  registryFields: COOLING_READINESS_FIELDS,
  rbacAnchorDataField: 'coolingIsChilledWaterAvailable',
  subsectionId: 'coolingInformation',
});

const liquidCoolingCduEditorSummaryRows = buildEditorSubsectionSummaryRows({
  editorFields: editorSubsectionFieldsExcluding(
    LIQUID_COOLING_CDU_EDITOR_SUBSECTION_FIELDS,
    LIQUID_COOLING_CDU_EDITOR_SHARED_FIELD_SET,
  ),
  registryFields: LIQUID_COOLING_FIELDS,
  rbacAnchorDataField: 'lcCduType',
  subsectionId: 'project',
});

const liquidCoolingCduEditorSharedSummaryRows =
  buildEditorSubsectionSummaryRows({
    editorFields: LIQUID_COOLING_CDU_EDITOR_SHARED_FIELD_KEYS.map(key => ({
      key,
      shortLabel:
        LIQUID_COOLING_FIELDS.find(field => field.dataField === key)
          ?.shortLabel ?? key,
    })),
    registryFields: LIQUID_COOLING_FIELDS,
    rbacAnchorDataField: 'lcLiquidToChipDesired',
    subsectionId: 'project',
  });

export function getLargeUpsEditorSummaryDefinitions(): SummaryCardRow[] {
  return largeUpsEditorSummaryRows;
}

export function getPowerDistributionEditorSummaryDefinitions(): SummaryCardRow[] {
  return powerDistributionEditorSummaryRows;
}

export function getSmallUpsEditorSummaryDefinitions(): SummaryCardRow[] {
  return smallUpsEditorSummaryRows;
}

export function getCoolingSystemEditorSummaryDefinitions(): SummaryCardRow[] {
  return coolingSystemEditorSummaryRows;
}

export function getCoolingEditorSharedSummaryDefinitions(): SummaryCardRow[] {
  return coolingEditorSharedSummaryRows;
}

export function getLiquidCoolingCduEditorSummaryDefinitions(): SummaryCardRow[] {
  return liquidCoolingCduEditorSummaryRows;
}

export function getLiquidCoolingCduEditorSharedSummaryDefinitions(): SummaryCardRow[] {
  return liquidCoolingCduEditorSharedSummaryRows;
}
export const LIQUID_COOLING_NOTES_SUBSECTION_FIELDS = subsectionDescriptors(
  LIQUID_COOLING_FIELDS,
  'additionalInformation',
);

/**
 * #997 Bug 3 — canonical Summary badge metric field lists = per-unit editor fields
 * + the per-instance "Additional Information" note, so each unit's note counts toward
 * the Summary accordion filled/total (Racks parity). Single source of truth consumed by
 * `Summary.tsx` (via `configurationListHeaderMetricsProps`) and the badge-parity guard.
 * Kept SEPARATE from `*_EDITOR_SUBSECTION_FIELDS` so the editor's own rendering and badge
 * counts are unchanged; the note is exactly one extra descriptor per unit.
 */
export const LARGE_UPS_SUMMARY_METRIC_FIELDS: readonly SubsectionFieldDescriptor[] =
  [
    ...LARGE_UPS_EDITOR_SUBSECTION_FIELDS,
    { key: 'powerAdditionalInformation', shortLabel: 'Notes' },
  ];
export const POWER_DISTRIBUTION_SUMMARY_METRIC_FIELDS: readonly SubsectionFieldDescriptor[] =
  [
    ...POWER_DISTRIBUTION_EDITOR_SUBSECTION_FIELDS,
    { key: 'powerAdditionalInformation', shortLabel: 'Notes' },
  ];
export const SMALL_UPS_SUMMARY_METRIC_FIELDS: readonly SubsectionFieldDescriptor[] =
  [
    ...SMALL_UPS_EDITOR_SUBSECTION_FIELDS,
    { key: 'powerSmallUpsAdditionalInfo', shortLabel: 'Notes' },
  ];
export const COOLING_SYSTEM_SUMMARY_METRIC_FIELDS: readonly SubsectionFieldDescriptor[] =
  [
    ...COOLING_SYSTEM_EDITOR_SUBSECTION_FIELDS,
    { key: 'coolingAdditionalReadinessInfo', shortLabel: 'Notes' },
  ];
export const LIQUID_COOLING_CDU_SUMMARY_METRIC_FIELDS: readonly SubsectionFieldDescriptor[] =
  [
    ...LIQUID_COOLING_CDU_EDITOR_SUBSECTION_FIELDS,
    { key: 'lcAdditionalInformationSfn', shortLabel: 'Notes' },
  ];

/**
 * #997 — Per-instance "Additional Information" note rows for repeatable sections.
 * Appended AFTER the per-unit editor rows in each section's summary definitions so
 * every unit shows its own note (em-dash when empty). Built as a standalone row set
 * (NOT added to `*_EDITOR_SUBSECTION_FIELDS`) so editor rendering/badges are unchanged.
 * LUPS/PD keep the section-specific labels the removed request-level flat row used.
 */
const largeUpsNoteSummaryRows = buildEditorSubsectionSummaryRows({
  editorFields: LARGE_UPS_NOTES_SUBSECTION_FIELDS,
  registryFields: FACILITY_POWER_FIELDS,
  rbacAnchorDataField: 'powerAdditionalInformation',
  subsectionId: 'additionalInformation',
  labelOverrides: { powerAdditionalInformation: 'Additional Large UPS Info' },
});

const powerDistributionNoteSummaryRows = buildEditorSubsectionSummaryRows({
  editorFields: FACILITY_POWER_NOTES_SUBSECTION_FIELDS,
  registryFields: FACILITY_POWER_FIELDS,
  rbacAnchorDataField: 'powerAdditionalInformation',
  subsectionId: 'additionalInformation',
  labelOverrides: {
    powerAdditionalInformation: 'Additional Power Distribution Info',
  },
});

const smallUpsNoteSummaryRows = buildEditorSubsectionSummaryRows({
  editorFields: SMALL_UPS_NOTES_SUBSECTION_FIELDS,
  registryFields: SMALL_UPS_FIELDS,
  rbacAnchorDataField: 'powerSmallUpsAdditionalInfo',
  subsectionId: 'additionalInformation',
});

const coolingSystemNoteSummaryRows = buildEditorSubsectionSummaryRows({
  editorFields: COOLING_NOTES_SUBSECTION_FIELDS,
  registryFields: COOLING_READINESS_FIELDS,
  rbacAnchorDataField: 'coolingAdditionalReadinessInfo',
  subsectionId: 'additionalInformation',
});

const liquidCoolingCduNoteSummaryRows = buildEditorSubsectionSummaryRows({
  editorFields: LIQUID_COOLING_NOTES_SUBSECTION_FIELDS,
  registryFields: LIQUID_COOLING_FIELDS,
  rbacAnchorDataField: 'lcAdditionalInformationSfn',
  subsectionId: 'additionalInformation',
});

export function getLargeUpsNoteSummaryDefinitions(): SummaryCardRow[] {
  return largeUpsNoteSummaryRows;
}

export function getPowerDistributionNoteSummaryDefinitions(): SummaryCardRow[] {
  return powerDistributionNoteSummaryRows;
}

export function getSmallUpsNoteSummaryDefinitions(): SummaryCardRow[] {
  return smallUpsNoteSummaryRows;
}

export function getCoolingSystemNoteSummaryDefinitions(): SummaryCardRow[] {
  return coolingSystemNoteSummaryRows;
}

export function getLiquidCoolingCduNoteSummaryDefinitions(): SummaryCardRow[] {
  return liquidCoolingCduNoteSummaryRows;
}

export const LIQUID_COOLING_SYSTEM_SUBSECTION_FIELDS = subsectionDescriptors(
  LIQUID_COOLING_FIELDS,
  'systemLevel',
);
export const LIQUID_COOLING_PRIMARY_SUBSECTION_FIELDS = subsectionDescriptors(
  LIQUID_COOLING_FIELDS,
  'primaryLoop',
);
export const LIQUID_COOLING_SECONDARY_SUBSECTION_FIELDS = subsectionDescriptors(
  LIQUID_COOLING_FIELDS,
  'secondaryLoop',
);

export const MONITORING_MAIN_SUBSECTION_FIELDS = subsectionDescriptors(
  MONITORING_MANAGEMENT_FIELDS,
  'monitoringInformation',
).filter(f => f.key === 'mgmtDescribeMonitoringControlSoftwareNeeds');
export const MONITORING_NOTES_SUBSECTION_FIELDS = [
  { key: 'mgmtAdditionalRemoteMgmtInfo', shortLabel: 'Notes' },
] as const;

const opportunitySummaryRegistryRows =
  buildSummaryRowsFromFields(OPPORTUNITY_FIELDS);
const opportunitySummaryRowsEnriched = enrichSummaryRowsWithRbac(
  opportunitySummaryRegistryRows,
);

export function getOpportunitySummaryRegistryRows() {
  return opportunitySummaryRegistryRows;
}

export function getOpportunitySummaryRowsWithRbac() {
  return opportunitySummaryRowsEnriched;
}

export function getOpportunitySummaryFieldMappingsFromRegistry() {
  return buildSummaryFieldMappingsFromRows(opportunitySummaryRowsEnriched);
}

export function getOpportunitySummaryDefinitions() {
  return getOpportunitySummaryRowsWithRbac();
}

export function getOpportunitySummaryFieldMappings() {
  return getOpportunitySummaryFieldMappingsFromRegistry();
}

export function getOpportunitySummaryRowTestIdsInOrder(): string[] {
  return getOpportunitySummaryRowsWithRbac().map(
    d => `summary-opportunity-row-${d.dataField}`,
  );
}

export const siteInformationSummaryBridge = createStepSummaryBridge(
  SITE_INFORMATION_FIELDS,
);
export const facilityPowerSummaryBridge = createStepSummaryBridge(
  FACILITY_POWER_FIELDS,
);
export const smallUpsSummaryBridge = createStepSummaryBridge(SMALL_UPS_FIELDS);
export const coolingReadinessSummaryBridge = createStepSummaryBridge(
  COOLING_READINESS_FIELDS,
);
export const liquidCoolingSummaryBridge = createStepSummaryBridge(
  LIQUID_COOLING_FIELDS,
);
export const monitoringSummaryBridge = createStepSummaryBridge(
  MONITORING_MANAGEMENT_FIELDS,
);

export function getSiteInformationSummaryDefinitions() {
  return siteInformationSummaryBridge.getSummaryDefinitions();
}
export function getSiteInformationSummaryFieldMappings() {
  return siteInformationSummaryBridge.getSummaryFieldMappings();
}
export function getSiteInformationSummaryRowTestIdsInOrder() {
  return siteInformationSummaryBridge.getSummaryRowTestIdsInOrder(
    'summary-site-information-row-',
  );
}

export function getFacilityPowerSummaryDefinitions() {
  return facilityPowerSummaryBridge.getSummaryDefinitions();
}
export function getFacilityPowerSummaryFieldMappings() {
  return facilityPowerSummaryBridge.getSummaryFieldMappings();
}
export function getFacilityPowerSummaryRowTestIdsInOrder() {
  return facilityPowerSummaryBridge.getSummaryRowTestIdsInOrder(
    'summary-facility-power-row-',
  );
}

export function getRacksSummaryDefinitions() {
  return racksSummaryBridge.getSummaryDefinitions();
}
export function getRacksSummaryFieldMappings() {
  return racksSummaryBridge.getSummaryFieldMappings();
}
export function getRacksSummaryRowTestIdsInOrder() {
  return racksSummaryBridge.getSummaryRowTestIdsInOrder('summary-racks-row-');
}

export function getSmallUpsSummaryDefinitions() {
  return smallUpsSummaryBridge.getSummaryDefinitions();
}
export function getSmallUpsSummaryFieldMappings() {
  return smallUpsSummaryBridge.getSummaryFieldMappings();
}
export function getSmallUpsSummaryRowTestIdsInOrder() {
  return smallUpsSummaryBridge.getSummaryRowTestIdsInOrder(
    'summary-small-ups-row-',
  );
}

export function getCoolingReadinessSummaryDefinitions() {
  return coolingReadinessSummaryBridge.getSummaryDefinitions();
}
export function getCoolingReadinessSummaryFieldMappings() {
  return coolingReadinessSummaryBridge.getSummaryFieldMappings();
}
export function getCoolingReadinessSummaryRowTestIdsInOrder() {
  return coolingReadinessSummaryBridge.getSummaryRowTestIdsInOrder(
    'summary-cooling-row-',
  );
}

export function getLiquidCoolingSummaryDefinitions() {
  return liquidCoolingSummaryBridge.getSummaryDefinitions();
}
export function getLiquidCoolingSummaryFieldMappings() {
  return liquidCoolingSummaryBridge.getSummaryFieldMappings();
}
export function getLiquidCoolingSummaryRowTestIdsInOrder() {
  return liquidCoolingSummaryBridge.getSummaryRowTestIdsInOrder(
    'summary-liquid-cooling-row-',
  );
}

export function getMonitoringSummaryDefinitions() {
  return monitoringSummaryBridge.getSummaryDefinitions();
}
export function getMonitoringSummaryFieldMappings() {
  return monitoringSummaryBridge.getSummaryFieldMappings();
}
export function getMonitoringSummaryRowTestIdsInOrder() {
  return monitoringSummaryBridge.getSummaryRowTestIdsInOrder(
    'summary-monitoring-row-',
  );
}

/** Rack Information / Specs field order (step cards + modal). */
export const RACK_INFORMATION_DATA_FIELDS = [
  ...RACK_INFORMATION_SUBSECTION_FIELDS.map(f => f.key),
] as const;
export const RACK_SPECS_DATA_FIELDS = [
  ...RACK_SPECS_SUBSECTION_FIELDS.map(f => f.key),
] as const;

/** Facility Power step fields excluded from the multi-rack modal Rack Power block (#995). */
export const RACK_MODAL_FACILITY_POWER_FIELDS_EXCLUDED = [
  'powerAverageItLoadPerRackKw',
  'powerMaximumItLoadPerRackKw',
  'powerNumberAndTypeInputPerRack',
] as const;

export const RACK_MODAL_RACKS_FIELDS = [
  { key: 'rackName', shortLabel: 'Name' },
  { key: 'rackStatus', shortLabel: 'Type' },
  // locationTagId comes from RACK_INFORMATION_SUBSECTION_FIELDS (do not prepend —
  // a second entry duplicated the Summary/editor Location row).
  ...RACK_INFORMATION_SUBSECTION_FIELDS,
  ...RACK_SPECS_SUBSECTION_FIELDS,
] as const;

export const RACK_MODAL_POWER_FIELDS = RACK_POWER_INFO_SUBSECTION_FIELDS.filter(
  f =>
    !RACK_MODAL_FACILITY_POWER_FIELDS_EXCLUDED.includes(
      f.key as (typeof RACK_MODAL_FACILITY_POWER_FIELDS_EXCLUDED)[number],
    ),
);

export const RACK_MODAL_ADDITIONAL_FIELDS = RACK_POWER_NOTES_SUBSECTION_FIELDS;

/** Matches RackEditFormContent cards — Racks, Rack Power Information, Additional Information. */
type RackFormSummarySubsection =
  | 'racks'
  | 'rackPowerInformation'
  | 'additionalInformation';

const RACK_MODAL_RACKS_FIELD_KEYS = new Set(
  RACK_MODAL_RACKS_FIELDS.map(f => f.key),
);
const RACK_MODAL_POWER_FIELD_KEYS = new Set(
  RACK_MODAL_POWER_FIELDS.map(f => f.key),
);
const RACK_REGISTRY_LABEL_BY_FIELD = new Map(
  RACK_POWER_FIELDS.map(field => [field.dataField, field.label]),
);

function rackFormSummarySubsection(
  dataField: string,
): RackFormSummarySubsection {
  if (dataField === 'rackAdditionalRackInformation') {
    return 'additionalInformation';
  }
  if (RACK_MODAL_POWER_FIELD_KEYS.has(dataField)) {
    return 'rackPowerInformation';
  }
  return 'racks';
}

function rackFormSummaryLabel(dataField: string, shortLabel?: string): string {
  if (dataField === 'rackName') {
    return 'Rack Name';
  }
  if (dataField === 'rackStatus') {
    return RACK_EDIT_RACK_TYPE_LABEL;
  }
  return (
    RACK_REGISTRY_LABEL_BY_FIELD.get(dataField) ??
    RACK_POWER_FIELD_LABELS[
      dataField as keyof typeof RACK_POWER_FIELD_LABELS
    ] ??
    shortLabel ??
    dataField
  );
}

/** Field order matches RackEditFormContent subsection cards (Racks + Power + Additional). */
export const RACK_FORM_SUMMARY_FIELD_ORDER: readonly {
  dataField: string;
  shortLabel?: string;
}[] = [
  ...RACK_MODAL_RACKS_FIELDS.map(field => ({
    dataField: field.key,
    shortLabel: field.shortLabel,
  })),
  ...RACK_MODAL_POWER_FIELDS.map(field => ({
    dataField: field.key,
    shortLabel: field.shortLabel,
  })),
  ...RACK_MODAL_ADDITIONAL_FIELDS.map(field => ({
    dataField: field.key,
    shortLabel: field.shortLabel,
  })),
];

/** Per-unit field total — 15 Racks + 15 Power + 1 Additional (#995; Location once). */
export const RACK_FORM_FIELD_COUNT_PER_UNIT =
  RACK_FORM_SUMMARY_FIELD_ORDER.length;

function resolveRbacForRackFormSummaryField(dataField: string): {
  permissionSection: string;
  permissionField: string;
} {
  const rbac = resolveRbacKeysForDataField(dataField);
  if (rbac) {
    return rbac;
  }
  const platformRbac = resolveRbacKeysForDataField('rackPlatform');
  if (!platformRbac) {
    throw new Error(
      `No fieldMapping entry for rack summary dataField=${dataField}`,
    );
  }
  return platformRbac;
}

function buildRackFormSummaryRowDefinitions(): SummaryCardRow[] {
  const seenSubsections = new Set<RackFormSummarySubsection>();
  return RACK_FORM_SUMMARY_FIELD_ORDER.map(({ dataField, shortLabel }) => {
    const subsection = rackFormSummarySubsection(dataField);
    const subsectionMarker = seenSubsections.has(subsection)
      ? undefined
      : subsection;
    seenSubsections.add(subsection);
    const rbac = resolveRbacForRackFormSummaryField(dataField);
    return {
      dataField,
      label: rackFormSummaryLabel(dataField, shortLabel),
      subsection: subsectionMarker,
      permissionSection: rbac.permissionSection,
      permissionField: rbac.permissionField,
    };
  });
}

const rackFormSummaryRows = buildRackFormSummaryRowDefinitions();

export function getRackFormSummaryDefinitions(): SummaryCardRow[] {
  return rackFormSummaryRows;
}

export const racksSummaryBridge = {
  getSummaryDefinitions: getRackFormSummaryDefinitions,
  getSummaryFieldMappings: () =>
    buildSummaryFieldMappingsFromRows(getRackFormSummaryDefinitions()),
  getSummaryRowTestIdsInOrder: (prefix: string) =>
    getRackFormSummaryDefinitions().map(row => `${prefix}${row.dataField}`),
};

export function getAllRegistryFields(): readonly RequestFieldDefinition[] {
  return [
    ...OPPORTUNITY_FIELDS,
    ...SITE_INFORMATION_FIELDS,
    ...FACILITY_POWER_FIELDS,
    ...RACK_POWER_FIELDS,
    ...MONITORING_MANAGEMENT_FIELDS,
    ...SMALL_UPS_FIELDS,
    ...COOLING_READINESS_FIELDS,
    ...LIQUID_COOLING_FIELDS,
  ];
}
